import React, { useMemo } from "react";
import {
  Grid,
  Box,
  Typography,
  useTheme,
  List,
  ListItem,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import LinkCard from "./LinkCard";

const useStyles = makeStyles(theme => ({
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8, 2),
    color: "#666",
  },
  listItem: {
    marginBottom: theme.spacing(2),
    padding: 0,
  },
}));

const LinksList = React.memo(
  ({
    links,
    loading,
    userAdmin,
    page,
    setPage,
    viewMode,
    onOpenLink,
    onEditLink,
    onDeleteLink,
  }) => {
    const classes = useStyles();
    const theme = useTheme();

    // Memoized pagination calculations
    const paginationConfig = useMemo(() => {
      const linksPerPage = viewMode === "list" ? 8 : 9; // Fewer items in list view
      const totalLinks = links.length;
      const totalPages = Math.ceil(totalLinks / linksPerPage);
      const paginatedLinks = links.slice(
        (page - 1) * linksPerPage,
        page * linksPerPage
      );

      return { linksPerPage, totalLinks, totalPages, paginatedLinks };
    }, [links, page, viewMode]);

    // Memoized date formatting
    const formatDate = useMemo(
      () => dateString => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toISOString().split("T")[0].replace(/-/g, "/");
      },
      []
    );

    // Loading state
    if (loading) {
      return (
        <Typography
          variant="h6"
          align="center"
          style={{ padding: theme.spacing(4) }}
          aria-live="polite"
        >
          Loading links...
        </Typography>
      );
    }

    // Empty state
    if (links.length === 0) {
      return (
        <Typography
          variant="h6"
          className={classes.emptyState}
          aria-live="assertive"
        >
          No links found.{" "}
          {userAdmin && "Add your first link using the 'Add Link' button."}
        </Typography>
      );
    }

    // Render grid view
    if (viewMode === "grid") {
      return (
        <>
          <Grid container spacing={3} aria-label="Links grid">
            {paginationConfig.paginatedLinks.map(link => (
              <Grid item xs={12} sm={6} md={4} key={link.id}>
                <LinkCard
                  title={link.link_name}
                  description={link.link_address}
                  tag={
                    link.rest_var && link.rest_var.length > 0
                      ? link.rest_var[0].q
                      : "No parameters"
                  }
                  usageCount={link.clickCount || 0}
                  usageGoal={5}
                  lastUsed={formatDate(link.lastClicked)}
                  onOpen={() => onOpenLink(link)}
                  onEdit={() => onEditLink(link)}
                  onDelete={() => onDeleteLink(link)}
                  isActive={link.clickCount > 0}
                  createdDate={link.created_at}
                  createdBy={link.created_by}
                  linkUrl={link.link_address}
                  viewMode="grid"
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {paginationConfig.totalPages > 1 && (
            <Box
              className={classes.paginationContainer}
              aria-label="Pagination navigation"
            >
              <Pagination
                count={paginationConfig.totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
                aria-label="Change page"
              />
            </Box>
          )}
        </>
      );
    }

    // Render list view
    return (
      <>
        <List aria-label="Links list">
          {paginationConfig.paginatedLinks.map(link => (
            <ListItem className={classes.listItem} key={link.id} disableGutters>
              <LinkCard
                title={link.link_name}
                description={link.link_address}
                tag={
                  link.rest_var && link.rest_var.length > 0
                    ? link.rest_var[0].q
                    : "No parameters"
                }
                usageCount={link.clickCount || 0}
                usageGoal={5}
                lastUsed={formatDate(link.lastClicked)}
                onOpen={() => onOpenLink(link)}
                onEdit={() => onEditLink(link)}
                onDelete={() => onDeleteLink(link)}
                isActive={link.clickCount > 0}
                createdDate={link.created_at}
                createdBy={link.created_by}
                linkUrl={link.link_address}
                viewMode="list"
              />
            </ListItem>
          ))}
        </List>

        {/* Pagination */}
        {paginationConfig.totalPages > 1 && (
          <Box
            className={classes.paginationContainer}
            aria-label="Pagination navigation"
          >
            <Pagination
              count={paginationConfig.totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              aria-label="Change page"
            />
          </Box>
        )}
      </>
    );
  }
);

export default LinksList;
